import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import { Icon } from '@/components/entities/Sprite';
import handleCopyText from '@/libs/handleCopyText';
import { cn } from '@/utils/cn';

const formatUserIdForDisplayMobile = (id: string) => {
  return `${id.slice(0, 4)}-${id.slice(4, 8)}...${id.slice(-8, -4)}-${id.slice(-4)}`;
};

const fieldsVariants = cva('flex w-full flex-col', {
  variants: {
    appearance: {
      profileCard: 'gap-3',
      gmMobile: 'gap-2.5',
    },
  },
  defaultVariants: {
    appearance: 'profileCard',
  },
});

const fieldVariants = cva(
  'flex w-full items-center gap-2.5 rounded-[6.25rem] bg-c900 px-4 text-white outline outline-main-800 -outline-offset-1',
  {
    variants: {
      appearance: {
        profileCard: 'h-11 py-3 paragraph-s-regular',
        gmMobile: 'h-9 paragraph-s-regular',
      },
    },
    defaultVariants: {
      appearance: 'profileCard',
    },
  },
);

interface ProfileEmailAndIdFieldsProps extends VariantProps<typeof fieldsVariants> {
  className?: string;
  email?: string;
  userId?: string;
}

export const ProfileEmailAndIdFields = ({
  appearance,
  className,
  email,
  userId,
}: ProfileEmailAndIdFieldsProps) => {
  const t = useTranslations('user-profile.information');

  if (!email || !userId) {
    return null;
  }

  const handleCopyUserId = () => {
    handleCopyText('info', userId, t('user-id-copied'), 'items-center px-6', true);
  };

  return (
    <div className={cn(fieldsVariants({ appearance }), className)}>
      <div
        title={email}
        className={fieldVariants({ appearance })}
      >
        <p className="min-w-0 truncate text-white">
          {email}
        </p>
        <div className="ml-auto">
          <Icon name="success" className="size-4 text-main-success" />
        </div>
      </div>

      <button
        type="button"
        title={userId}
        onClick={handleCopyUserId}
        className={fieldVariants({ appearance })}
      >
        <span className="min-w-0 truncate">
          {t('user-id')}
          {' '}
          {formatUserIdForDisplayMobile(userId)}
        </span>
        <div className="ml-auto">
          <Icon name="copy" className="size-4 text-c300 transition-all duration-300 hover:scale-110 hover:text-white" />
        </div>
      </button>
    </div>
  );
};
